/**
 * Dedicated full-viewport chat with Alfred about a specific ticket.
 *
 * Navigated to from the ticket detail page when the user wants more
 * horizontal space than the inline chat panel can offer (ticket detail
 * shares the page with sidebar / PR badges / branch readout).
 *
 * URL is bookmarkable + browser-back returns to the ticket detail.
 */
import * as React from "react";
import {
  createFileRoute,
  Link,
  useParams,
} from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { ArrowLeft, ArrowSquareOut, CircleNotch } from "@phosphor-icons/react";
import { api } from "@alfred/convex/_generated/api";
import { Badge } from "~/components/ui/badge";
import { TicketChat } from "~/components/TicketChat";
import { KANBAN_META } from "~/lib/kanban";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/tickets/$key/chat")({
  component: ChatPage,
});

function ChatPage() {
  const { key } = useParams({ strict: false }) as { key: string };
  const ticket = useQuery(api.tickets.get, { jiraKey: key });
  const runs = useQuery(
    api.runs.listForTicket,
    ticket ? { ticketId: ticket._id } : "skip",
  );

  if (ticket === undefined) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <CircleNotch size={16} className="mr-2 animate-spin" /> Loading {key}…
      </div>
    );
  }
  if (ticket === null) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <h2 className="text-lg font-semibold">Ticket {key} not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Alfred hasn't seen this ticket yet.
        </p>
      </div>
    );
  }

  const activeRun =
    runs?.find(
      (r) =>
        r.status !== "succeeded" &&
        r.status !== "failed" &&
        r.status !== "cancelled",
    ) ??
    runs?.[0] ??
    null;

  const meta = KANBAN_META[ticket.status as keyof typeof KANBAN_META];

  return (
    // Full viewport (minus the global sidebar nav). flex-col so chat
    // input pins to the bottom; the chat stream owns the remaining
    // height and scrolls internally.
    <div className="flex h-full min-h-0 w-full flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur">
        <Link
          to="/tickets/$key"
          params={{ key }}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Back to ticket"
          title="Back to ticket"
        >
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="font-mono">
              {ticket.jiraKey}
            </Badge>
            {meta ? (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                  meta.accent,
                )}
              >
                {meta.label}
              </span>
            ) : null}
            <a
              href={ticket.jiraUrl}
              target="_blank"
              rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary"
            >
              Open in Jira <ArrowSquareOut size={11} weight="bold" />
            </a>
          </div>
          <h1 className="mt-0.5 truncate text-base font-semibold tracking-tight">
            {ticket.title}
          </h1>
        </div>
      </header>

      {/* Chat itself. We deliberately don't cap the width here — the
          point of the dedicated route is to use ALL the horizontal
          space. The chat component handles internal layout (bubbles
          align to their side, max-width on individual bubbles). */}
      <div className="flex min-h-0 flex-1 flex-col">
        <TicketChat ticket={ticket} activeRun={activeRun} />
      </div>
    </div>
  );
}
